import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Input } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { useListContractsQuery } from '@shared/api';
import { contractStatusLabel } from '@shared/lib/statusLabels';
import type { Contract } from '@entities/legal/types';

const Wrap = styled.div`
  position: relative;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Selected = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  flex-wrap: wrap;
`;

const Dropdown = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding: ${({ theme }) => theme.spacing(1)};
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
  position: absolute;
  inset: 100% 0 auto 0;
  z-index: ${({ theme }) => theme.z.overlay};
`;

const Option = styled.li<{ $active: boolean; $current: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme, $active }) => ($active ? theme.colors.surface : 'transparent')};
  outline: ${({ theme, $current }) =>
    $current ? `1px dashed ${theme.colors.primary}` : 'none'};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const OptionMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const OptionTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Hint = styled.div<{ $tone?: 'info' | 'warn' }>`
  font-size: 12px;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $tone }) =>
    $tone === 'warn' ? theme.colors.status.warning + '14' : theme.colors.surface};
  color: ${({ theme, $tone }) =>
    $tone === 'warn' ? theme.colors.status.warning : theme.colors.text.secondary};
  border-left: 3px solid
    ${({ theme, $tone }) =>
      $tone === 'warn' ? theme.colors.status.warning : theme.colors.border};
  line-height: 1.4;
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const LIST_LIMIT = 200;
const VISIBLE_LIMIT = 20;

interface ContractPickerProps {
  /** Если задан — фильтрует список договоров только по этому контрагенту. */
  counterpartyId?: string | null;
  /** Текущий выбранный contract_id (если уже связан). */
  value?: string | null;
  /** Колбэк при выборе договора. Второй аргумент — человекочитаемый label
   *  для inline-подтверждения (counterparty + subject), чтобы caller'у
   *  не нужно было снова запрашивать contract details для отображения. */
  onSelect: (contractId: string, label?: string) => void;
  /** Disabled state — пока идёт mutation. */
  disabled?: boolean;
}

function formatSignedAt(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function matchesQuery(c: Contract, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  if (!needle) return true;
  const haystack = [c.counterparty, c.subject ?? '', c.id].join(' ').toLowerCase();
  return haystack.includes(needle);
}

/**
 * Inline-комбобокс для выбора договора. Используется в карточке претензии/иска
 * для привязки/изменения связанного договора.
 *
 * Стратегия фильтрации:
 * - Если задан counterpartyId — запрашиваем сразу `/contracts?counterparty_id=...`
 *   (backend поддерживает фильтр). Это маленький список.
 * - Без counterpartyId — тащим первые 200 договоров (sort=created_at desc),
 *   фильтруем по поисковому запросу на клиенте.
 *
 * Поиск: substring case-insensitive по `counterparty + subject + id`.
 */
export function ContractPicker({
  counterpartyId,
  value,
  onSelect,
  disabled,
}: ContractPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data, isFetching, isLoading } = useListContractsQuery({
    counterparty_id: counterpartyId ?? undefined,
    limit: LIST_LIMIT,
    sort: 'created_at',
    order: 'desc',
  });

  const allItems = useMemo(() => data?.items ?? [], [data?.items]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    return allItems.filter((c) => matchesQuery(c, query));
  }, [allItems, query]);

  const visible = useMemo(() => filtered.slice(0, VISIBLE_LIMIT), [filtered]);
  const hiddenCount = Math.max(0, filtered.length - VISIBLE_LIMIT);

  // Click-outside → закрыть dropdown.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Сброс активного индекса при изменении выдачи.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, allItems.length]);

  const currentSelected = useMemo(
    () => (value ? allItems.find((c) => c.id === value) ?? null : null),
    [allItems, value],
  );

  function handleSelect(c: Contract) {
    setOpen(false);
    setQuery('');
    const label = c.subject
      ? `${c.counterparty} — ${c.subject}`
      : c.counterparty;
    onSelect(c.id, label);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = visible[activeIndex];
      if (c) handleSelect(c);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  // Сценарий «у контрагента нет договоров вообще» — выявляем только когда
  // counterpartyId задан и сервер вернул пустой список без активного поиска.
  const counterpartyHasNoContracts =
    !!counterpartyId && !isLoading && !isFetching && allItems.length === 0;

  if (counterpartyHasNoContracts) {
    return (
      <Wrap ref={wrapRef}>
        <Input
          type="text"
          placeholder="Договоры контрагента не найдены"
          disabled
          aria-label="Выбор связанного договора"
        />
        <Hint $tone="warn">
          У этого контрагента нет договоров в реестре. Загрузите договор сначала
          — после этого можно будет привязать его к претензии.
        </Hint>
      </Wrap>
    );
  }

  return (
    <Wrap ref={wrapRef}>
      <SearchRow>
        <Input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={
            counterpartyId
              ? 'Найти договор по предмету или ID…'
              : 'Найти договор по контрагенту, предмету или ID…'
          }
          disabled={disabled}
          aria-label="Выбор связанного договора"
          aria-autocomplete="list"
          aria-expanded={open}
          role="combobox"
        />
      </SearchRow>

      {currentSelected && !open && (
        <Selected>
          <span style={{ fontWeight: 600 }}>{currentSelected.counterparty}</span>
          <span style={{ color: 'inherit', opacity: 0.7 }}>·</span>
          <span style={{ flex: 1, minWidth: 0 }}>{currentSelected.subject ?? '—'}</span>
          <Badge>{contractStatusLabel(currentSelected.status)}</Badge>
        </Selected>
      )}

      {open && (
        <Dropdown role="listbox" aria-label="Список договоров">
          {isLoading || isFetching ? (
            <Footer>Загружаю договоры…</Footer>
          ) : visible.length === 0 ? (
            <Footer>
              {query.trim()
                ? 'Ничего не нашлось. Попробуйте другой запрос.'
                : 'Договоров пока нет.'}
            </Footer>
          ) : (
            <>
              {visible.map((c, idx) => {
                const signed = formatSignedAt(c.signed_at);
                const isCurrent = value === c.id;
                return (
                  <Option
                    key={c.id}
                    role="option"
                    aria-selected={isCurrent}
                    $active={idx === activeIndex}
                    $current={isCurrent}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => handleSelect(c)}
                    tabIndex={-1}
                  >
                    <OptionMain>
                      <OptionTitle>{c.counterparty}</OptionTitle>
                      <OptionMeta>
                        {c.subject ?? 'без предмета'}
                        {signed ? ` · подписан ${signed}` : ''}
                      </OptionMeta>
                    </OptionMain>
                    <Badge>{contractStatusLabel(c.status)}</Badge>
                  </Option>
                );
              })}
              {hiddenCount > 0 && (
                <Footer>
                  Показаны первые {VISIBLE_LIMIT} из {filtered.length}. Уточните запрос,
                  чтобы увидеть остальные {hiddenCount}.
                </Footer>
              )}
            </>
          )}
        </Dropdown>
      )}
    </Wrap>
  );
}
